import clsx from "clsx";
import { ChevronRight } from "lucide-react";
import type { MouseEvent } from "react";
import { useHistory } from "react-router-dom";

export interface PageLinkBreadcrumbProps {
  /** The page's place in the app's navigation, e.g. `["Build", "File processing", "Test"]`. */
  trail: string[];
  /** The page's path. */
  to: string;
  /** Opens the page in a new tab, e.g. to keep a panel that only shows on the current page open. */
  newTab?: boolean;
  className?: string;
}

/** An inline link to a page, written as its breadcrumb (Build › File processing › Test). */
export function PageLinkBreadcrumb({ trail, to, newTab = false, className }: PageLinkBreadcrumbProps) {
  // Undefined outside a router (e.g. in isolated renders), where the link falls back to a normal navigation.
  const history = useHistory();
  const navigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (newTab || !history || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    history.push(to);
  };
  return (
    <a
      href={to}
      onClick={navigate}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noreferrer" : undefined}
      aria-label={`${trail.join(" › ")}${newTab ? " (opens in a new tab)" : ""}`}
      data-page-link-breadcrumb=""
      className={clsx(
        "inline-flex flex-wrap items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-px align-baseline",
        "text-[0.95em] font-medium leading-snug text-foreground no-underline transition-colors hover:border-foreground/30 hover:bg-muted",
        className,
      )}
    >
      {trail.map((label, index) => (
        <span key={`${index}-${label}`} className="inline-flex items-center gap-1">
          {index > 0 ? <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
          {label}
        </span>
      ))}
    </a>
  );
}
